import React, { useMemo } from 'react';

import BookingButton, {
  BookingButtonProps
} from 'common/components/ShowtimeModal/BookingButton';
import QuickInfo from 'common/components/ShowtimeModal/QuickInfo';
import { Proposal } from 'common/components/ShowtimeModal/hooks/useShowtimeData';
import useTechnoPremium from 'common/components/ShowtimeModal/hooks/useTechnoPremium';
import ShowtimeExperiences from 'common/components/Showtimes/ShowtimeExperiences';
import format, { HOURS_FORMATS } from 'common/tools/date/format';
import getVersionLabel, {
  getVersionGroupKey
} from 'common/tools/showtime/getVersionLabel';
import slugify from 'common/tools/string/slugify';
import trans from 'common/tools/translations/trans';

import styles from './styles.module.scss';

type BookingPremiumProps = Omit<
  BookingButtonProps,
  'experiences' | 'ticketingUrl'
> & {
  proposal: Proposal;
};

const genericOrLabel = trans('generic.or');
const alternativeLabel = trans('showtimes.premium.alternative');
const BookingPremium = ({ proposal, ...butttonProps }: BookingPremiumProps) => {
  const technoPremium = useTechnoPremium(proposal?.experiences ?? []);

  const tagline = useMemo(
    () =>
      technoPremium?.tag &&
      trans(`showtimes.premium.${slugify(technoPremium.name)}`),
    [technoPremium]
  );

  const startAt = useMemo(
    () =>
      proposal?.date &&
      `${format(proposal?.date, {
        weekday: 'long',
        day: '2-digit',
        month: 'long'
      })} - ${format(proposal.date, HOURS_FORMATS.SHORT)}
      `,
    [proposal?.date]
  );

  const versionLabel = useMemo(() => {
    if (!proposal?.diffusionVersion) {
      return undefined;
    }

    const key = getVersionGroupKey(
      proposal.diffusionVersion,
      proposal.tags ?? []
    );

    return getVersionLabel(key);
  }, [proposal]);

  return proposal?.ticketingUrl && technoPremium ? (
    <div className={styles.alternate}>
      <div className={styles.orSeparator}>{genericOrLabel}</div>
      <div className={styles.alternateShowtimes}>
        {alternativeLabel}
        <ShowtimeExperiences experiences={[technoPremium.tag]} />
      </div>
      <div>{tagline}</div>
      <div className={styles.alternateQuickInfo}>
        <QuickInfo info={startAt} />
        <QuickInfo info={versionLabel} />
      </div>
      <BookingButton
        experiences={proposal?.experiences}
        ticketingUrl={proposal.ticketingUrl}
        {...butttonProps}
      />
    </div>
  ) : null;
};

export default BookingPremium;
